/**
 * Live incoming-CC value store (MDL-269).
 *
 * Fed from the device's `parameterChanged` echo. Tracks two things per CC:
 *
 *   values[cc] — the raw value (0–127) the device is currently HOLDING for
 *                that CC. A mapped CC is a runtime override that persists on
 *                the device until superseded, so this persists too: it is
 *                only replaced by a newer value for the same CC, or dropped
 *                by clear(). This drives the knob ghost marker and the
 *                badge value readout.
 *
 *   active[cc] — true only while a CC is ARRIVING (short TTL, refreshed on
 *                every message). This drives the "moving right now" badge
 *                pulse and fades once the controller stops, while the held
 *                value stays put.
 *
 * Cardinal rule: this is DISPLAY-ONLY state. Nothing here may ever be
 * written into the patch model or mark the patch dirty — consumers render
 * transient indicators from it and nothing else.
 *
 * `parameterChanged` carries the device's receive channel, but the store is
 * keyed by CC alone: the firmware only echoes CCs it actually applied on
 * its own receive channel, and only one device is connected at a time.
 */

import { reactive } from 'vue';

export const CC_ACTIVE_TTL_MS = 800;

export function createCcLiveStore(activeTtlMs = CC_ACTIVE_TTL_MS) {
  const values = reactive({}); // cc -> held raw value 0-127 (persists)
  const active = reactive({}); // cc -> true while actively receiving (TTL'd)
  const timers = {};

  function feed(cc, value) {
    values[cc] = value;
    active[cc] = true;
    clearTimeout(timers[cc]);
    timers[cc] = setTimeout(() => {
      // Activity lapses, but the held value stays until superseded — the
      // device is still applying it.
      delete active[cc];
      delete timers[cc];
    }, activeTtlMs);
  }

  // Drop everything immediately — call on disconnect, WS reconnect and patch
  // change, where the device's overrides are gone or unknown, so no stale
  // value is ever displayed (same rule as the tempo display).
  function clear() {
    for (const cc of Object.keys(timers)) {
      clearTimeout(timers[cc]);
      delete timers[cc];
    }
    for (const cc of Object.keys(values)) delete values[cc];
    for (const cc of Object.keys(active)) delete active[cc];
  }

  return { values, active, feed, clear };
}
